import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicById, getTrendingComics } from '@/mocks/comics';
import { generateChapters } from '@/mocks/articles';
import type { ArticleChapter } from '@/mocks/articles';
import { useState, useEffect } from 'react';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '0', 10);
  const comic = getComicById(comicId);
  const [chapters, setChapters] = useState<ArticleChapter[]>([]);
  const [showAllChapters, setShowAllChapters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const chaptersPerPage = 20;

  useEffect(() => {
    if (comic) {
      const allChapters = generateChapters(comic.id, comic.chapters);
      setChapters(allChapters);
    }
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const totalPages = Math.ceil(chapters.length / chaptersPerPage);
  const displayedChapters = showAllChapters
    ? chapters.slice((currentPage - 1) * chaptersPerPage, currentPage * chaptersPerPage)
    : chapters.slice(0, 10);

  const categoryMap: Record<string, string> = {
    romance: '恋爱',
    fantasy: '奇幻',
    action: '动作',
    thriller: '悬疑',
    comedy: '搞笑',
    drama: '剧情',
    horror: '恐怖',
    daily: '日常',
    historical: '历史',
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 虚空韩漫网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2025-06-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '虚空韩漫网',
    },
  };

  const trending = getTrendingComics().filter((c) => c.id !== comic.id).slice(0, 5);

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 虚空韩漫网_免费韩漫在线阅读`}
        description={`虚空韩漫网提供${comic.title}高清下拉式全集免费阅读。${comic.description}作者：${comic.author}，评分：${comic.rating}，共${comic.chapters}话。`}
        keywords={`${comic.title}, ${comic.author}, 虚空韩漫网, 免费韩漫, 韩漫在线阅读, 高清韩漫`}
        canonicalUrl={`https://xukonghanman.com/article/${comic.id}`}
        ogType="article"
        schema={articleSchema}
        breadcrumbItems={[
          { name: '首页', url: 'https://xukonghanman.com/' },
          { name: categoryMap[comic.category] || comic.category, url: `https://xukonghanman.com/category/${comic.category}` },
          { name: comic.title, url: `https://xukonghanman.com/article/${comic.id}` },
        ]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', url: '/' },
            { name: categoryMap[comic.category] || comic.category, url: `/category/${comic.category}` },
            { name: comic.title },
          ]}
        />

        <article className="w-full max-w-[1200px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-[280px] shrink-0">
              <div className="rounded-xl overflow-hidden aspect-[5/7] sticky top-24">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 虚空韩漫网`}
                  title={`虚空韩漫网 - ${comic.title}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                {comic.title}
              </h1>

              <p className="text-sm text-foreground-500 mb-4">
                作者：<strong className="text-foreground-700">{comic.author}</strong>
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                  comic.status === '已完结'
                    ? 'bg-accent-100 text-accent-800'
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  <i className={comic.status === '已完结' ? 'ri-check-double-line' : 'ri-loader-4-line'}></i>
                  {comic.status}
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-800">
                  <i className="ri-book-open-line"></i>
                  {comic.chapters}话
                </span>
                <span className="inline-flex items-center gap-1 text-sm text-secondary-600">
                  <i className="ri-star-fill"></i>
                  <span className="font-semibold">{comic.rating}</span>
                  <span className="text-foreground-400">/ 10</span>
                </span>
                <span className="text-xs text-foreground-400">
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-100 text-foreground-600 rounded-md text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex items-center gap-3">
                <Link
                  to={`/article/${comic.id}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line"></i>
                  从第1话开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground-900 font-heading">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {comic.chapters} 话
                </span>
              </h2>
              {!showAllChapters && chapters.length > 10 && (
                <button
                  onClick={() => setShowAllChapters(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  查看全部章节
                  <i className="ri-arrow-down-s-line ml-1"></i>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {displayedChapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/article/${comic.id}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/20 transition-all cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {ch.title}
                  </span>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">{ch.updatedAt}</span>
                    <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                  </div>
                </Link>
              ))}
            </div>

            {showAllChapters && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      currentPage === i + 1
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </section>

          {trending.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">猜你喜欢</h2>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {trending.map((c) => (
                  <Link
                    key={c.id}
                    to={`/article/${c.id}`}
                    className="shrink-0 w-[120px] group cursor-pointer"
                  >
                    <div className="rounded-lg overflow-hidden aspect-[5/7] mb-2">
                      <img
                        src={c.cover}
                        alt={c.title}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {c.title}
                    </p>
                    <p className="text-[10px] text-foreground-500 mt-0.5">{c.rating}分</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}